/** @jsxImportSource theme-ui */

import React from 'react';
import { Icon } from '../../Icon';
import FlexContainer from '../../FlexContainer';
import { Label } from '../../Label';
import Stack from '../../Stack';
import { omit, pick } from '../../../styles/utils';
import { ErrorProps } from '../types/error';
import Text from '../../Text';

export interface ExtractableInputWrapperProps extends Omit<FormInputWrapperProps, 'disabled'>, ErrorProps {
    errorMessage?: string;
    count?: number;
    htmlFor?: string;
    couldHaveSubMessage?: boolean;
}

export interface FormInputWrapperProps {
    label?: string;
    helperMessage?: string;
    isRequired?: boolean;
    disabled?: boolean;
}

export interface InputWrapperProps extends ExtractableInputWrapperProps {
    displayCount?: boolean;
    maxLength?: number;
}

export const ExtractableInputWrapperPropsKeys = [
    'isRequired',
    'helperMessage',
    'errorMessage',
    'label',
    'count',
    'isError',
    'htmlFor',
    'couldHaveSubMessage',
];

export function extractWrapperProps<T>(props: T & ExtractableInputWrapperProps): {
    extractedProps: ExtractableInputWrapperProps;
    otherProps: T;
} {
    return {
        extractedProps: pick(props, ExtractableInputWrapperPropsKeys),
        otherProps: omit(props, ExtractableInputWrapperPropsKeys),
    };
}

/**
 * The amount of customizations on this component is getting pretty high. It might be time to
 * rethink how we're handling this shared behavior if the presentation is going to differ
 * so much between inputs.
 */

const InputWrapper = (props: React.PropsWithChildren<InputWrapperProps>): JSX.Element => {
    const {
        isRequired,
        htmlFor,
        isError,
        errorMessage,
        label,
        helperMessage,
        children,
        count,
        displayCount,
        couldHaveSubMessage,
        maxLength,
    } = props;

    return (
        <Stack spacing="4px">
            <div>
                <Label header={label} isRequired={isRequired} isError={isError} htmlFor={htmlFor}>
                    {htmlFor ? null : children}
                </Label>
                {htmlFor ? children : null}
            </div>
            {couldHaveSubMessage && (
                <FlexContainer justifyContent="space-between">
                    <FlexContainer>
                        <Text
                            weight="normal"
                            color={isError ? 'errorRed' : 'black60'}
                            size="small"
                            sx={{
                                lineHeight: '21px',
                                minHeight: '21px',
                                display: 'flex',
                                alignContent: 'center',
                            }}
                        >
                            {isError && errorMessage && <Icon size="tiny" name="x" />}
                            <Text
                                color={isError ? 'errorRed' : 'black60'}
                                size="small"
                                sx={{
                                    ml: 5,
                                }}
                            >
                                {!isError ? helperMessage : errorMessage}
                            </Text>
                        </Text>
                    </FlexContainer>
                    {displayCount && (
                        <Text
                            weight="normal"
                            color="black60"
                            size="small"
                            sx={{
                                minHeight: '21px',
                                lineHeight: '21px',
                                marginLeft: 'small',
                            }}
                        >
                            {count}/{maxLength}
                        </Text>
                    )}
                </FlexContainer>
            )}
        </Stack>
    );
};

InputWrapper.defaultProps = {
    count: 0,
    displayCount: false,
    couldHaveSubMessage: true,
    maxLength: Infinity,
};

export default InputWrapper;
